// CPGridV2 — a drawer-based data grid with external filtering, simplified adapter,
// and full-height/width layout. Replaces CPGrid v1's inline filter row with a slide-in
// drawer panel for filters (or any custom content).

import * as React from "react";
import classNames from "classnames";
import { Grid, GridColumn } from "@progress/kendo-react-grid";
import { ExcelExport } from "@progress/kendo-react-excel-export";
import { DataResult } from "@progress/kendo-data-query";

import "./CPGridV2Styles.scss";

import {
  CPGridV2Column,
  CPGridV2ColumnUpdate,
  CPGridV2CustomCellProps,
  CPGridV2Props,
} from "./types";

// Re-use stable sub-components from CPGrid v1 to avoid duplication
import CPLoader from "../CPGrid/CPLoader/CPLoader";
import CPEllipsisMenuCell from "../CPGrid/CPEllipsisMenuCell/CPEllipsisMenuCell";
import CPShowHideModal from "./CPShowHideModal/CPShowHideModal";
import CPGridV2Filters from "./CPGridV2Filters/CPGridV2Filters";

// Component
/**
 * CPGridV2 — a full-height/width data grid with a slide-in drawer panel.
 *
 * ## Key design decisions vs. CPGrid (v1)
 * - **No inline filter row** — `filterable` is always `false` on the Kendo Grid.
 *   Filters live in the left-side drawer and are applied externally.
 * - **One adapter** — `useCPGridV2` handles local sort + paging. Pass your
 *   already-filtered data array and the adapter does the rest.
 * - **Drawer-first UX** — the drawer can render auto-generated filter inputs
 *   (`filterOptions`) OR any custom content (`renderDrawer`). When closed,
 *   the grid occupies the full width.
 * @example
 * ```tsx
 * const adapter = useCPGridV2({ columns, data: filteredRows, showLoader: isFetching });
 *
 * <CPGridV2
 *   name="users-grid"
 *   title="Users"
 *   adapter={adapter}
 *   pageable
 *   compact
 *   gridLines="rows"
 *   filterOptions={[
 *     { filterType: "text",     label: "Username", columnKey: "username" },
 *     { filterType: "dropdown", label: "Status",   columnKey: "status",
 *       options: [{ label: "Active", value: 1 }, { label: "Inactive", value: 0 }] },
 *   ]}
 *   onApplyFilters={(f) => setFilters(f)}
 *   onClearFilters={() => setFilters({})}
 *   renderActions={(data) => (
 *     <CPButton themeColor="primary" onClick={handleAdd}>
 *       Add User
 *     </CPButton>
 *   )}
 *   onRefresh={() => refetch()}
 * />
 * ```
 */
const CPGridV2: React.FC<CPGridV2Props> = ({
  name,
  columns: columnsProp,
  records: recordsProp,
  adapter,
  title,
  renderActions,
  renderDrawer,
  filterOptions,
  onApplyFilters,
  onClearFilters,
  savedFilters,
  onSaveFilter,
  showSaveFilters,
  defaultDrawerOpen = false,
  drawerWidth = 280,
  pageable,
  paging,
  showLoader,
  noWrap,
  compact,
  gridLines,
  rowTitle,
  onRowClick,
  excelAdapter,
  showHideColumns = true,
  onRefresh,
  className,
  size,
  reorderable,
  resizable,
  rowRender,
  ...kendoGridProps
}) => {
  const [drawerOpen, setDrawerOpen] = React.useState(defaultDrawerOpen);
  const [showHideModalOpen, setShowHideModalOpen] = React.useState(false);
  const [activeFilterCount, setActiveFilterCount] = React.useState(0);

  // Tracks the last filter values sent via `onApplyFilters`, so a page/page-size/sort
  // change (which fires independently of the Apply button) can be re-sent to the
  // caller along with them instead of silently dropping the currently applied filters.
  const lastAppliedFiltersRef = React.useRef<Record<string, unknown>>({});

  // Adapter takes precedence; fall back to direct records prop.
  const records: DataResult = adapter?.records ??
    recordsProp ?? {
      data: [],
      total: 0,
    };
  const gridState = adapter?.gridState;
  const columns: CPGridV2Column[] =
    (columnsProp?.length ? columnsProp : adapter?.getColumnState()) ?? [];

  const hasDrawer =
    renderDrawer != null || (filterOptions != null && filterOptions.length > 0);

  React.useEffect(() => {
    if (!drawerOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setDrawerOpen(false);
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [drawerOpen]);

  const handleColumnUpdate = (state: CPGridV2ColumnUpdate) => {
    adapter?.onColumnUpdate(state);
  };

  const getPaging = () => {
    if (!pageable) return false;
    if (paging) return paging;
    return { buttonCount: 0, pageSizes: [10, 20, 50] };
  };

  // Re-sends the last-applied filters merged with the new paging/sort state.
  // Fired on page change, page-size change, and column sort — the same
  // events Kendo already surfaces via onDataStateChange/onSortChange —
  // so a refetch happens without requiring the Apply button.
  const notifyPagingChange = (controls: {
    take?: number;
    skip?: number;
    sort?: { field: string; dir?: "asc" | "desc" }[];
  }) => {
    if (onApplyFilters == null) return;
    const sort = controls.sort?.[0];
    onApplyFilters(lastAppliedFiltersRef.current, {
      take: controls.take,
      skip: controls.skip,
      sortBy: sort?.field,
      ascending: sort?.dir === "asc",
    });
  };

  const handleGridStateChange = (event: any) => {
    adapter?.onGridStateUpdate(event);
    notifyPagingChange(event.dataState);
  };

  const handleSortChange = (event: any) => {
    adapter?.onSort(event);
    // Sorting should also jump back to page 1, same as a new filter apply.
    notifyPagingChange({
      take: adapter?.gridState?.controls.take,
      skip: 0,
      sort: event.sort,
    });
  };

  const buildCustomCell = (column: CPGridV2Column) => {
    if (column.renderCell == null) return undefined;

    const CustomCell: React.FC<CPGridV2CustomCellProps> = (cellProps) => {
      const effectiveNoWrap = column.noWrap ?? noWrap;
      return (
        <td
          style={{ ...cellProps.style }}
          role="gridcell"
          colSpan={1}
          className={classNames("cpgv2-custom-cell", {
            "k-grid-content-sticky": column.pinned,
            "cpgv2-nowrap": effectiveNoWrap,
          })}
        >
          <div>{column.renderCell!(cellProps)}</div>
        </td>
      );
    };

    return CustomCell;
  };

  // Column renderer: handles pinned + menu columns, applies custom cell renderers, and sets
  const renderColumns = () => {
    const lockedCount = columns.filter(
      (c) => !c.hide && (c.pinned || c.columnType === "menu"),
    ).length;

    return columns
      .filter((c) => !c.hide)
      .map((column, i) => {
        // Ellipsis menu cell takes priority over renderCell
        let cellRenderer = buildCustomCell(column);

        if (column.columnType === "menu") {
          const EllipsisCell: React.FC<CPGridV2CustomCellProps> = (
            cellProps,
          ) => (
            <CPEllipsisMenuCell
              {...cellProps}
              onMenuOpen={column.onMenuOpen ?? (() => [])}
              onMenuItemClick={column.onMenuItemClick}
            />
          );
          cellRenderer = EllipsisCell;
        }

        return (
          <GridColumn
            {...column}
            key={i}
            filter={undefined}
            filterable={false}
            width={
              column.columnType === "menu" ? (column.width ?? 40) : column.width
            }
            minResizableWidth={column.minResizableWidth ?? 100}
            locked={column.columnType === "menu" ? true : column.pinned}
            reorderable={
              column.columnType === "menu"
                ? false
                : (column.reorderable ?? !column.pinned)
            }
            sortable={
              column.sortable ??
              (!column.pinned && column.columnType !== "menu")
            }
            resizable={
              column.columnType === "menu"
                ? false
                : (column.resizable ?? !column.pinned)
            }
            orderIndex={
              column.columnType === "menu"
                ? 0
                : column.pinned
                  ? 0
                  : lockedCount + (column.orderIndex ?? 1)
            }
            className={classNames({
              "cpgv2-nowrap": column.noWrap ?? noWrap,
            })}
            cell={cellRenderer}
          />
        );
      });
  };

  // Row render: clickable cursor + title tooltip
  const handleRowRender = (
    row: React.ReactElement,
    rowProps: { dataItem: any },
  ) => {
    const clickable = onRowClick != null;
    const titleValue =
      rowTitle != null
        ? typeof rowTitle === "function"
          ? rowTitle(rowProps.dataItem)
          : rowTitle
        : undefined;

    const extraProps: React.HTMLAttributes<HTMLTableRowElement> = {};
    if (clickable) extraProps.style = { ...row.props.style, cursor: "pointer" };
    if (titleValue) extraProps.title = titleValue;

    const augmented =
      clickable || titleValue ? React.cloneElement(row, extraProps) : row;

    return rowRender ? rowRender(augmented, rowProps as any) : augmented;
  };

  // Header
  const renderHeader = () => (
    <div className="cpgv2-header">
      {/* Left section: filter toggle + title */}
      <div className="cpgv2-header-left">
        {hasDrawer && (
          <div className="cpgv2-drawer-toggle-wrapper">
            <button
              type="button"
              className={classNames("cpgv2-icon-btn", {
                "cpgv2-icon-btn--active": drawerOpen,
              })}
              onClick={() => setDrawerOpen((prev) => !prev)}
              title={
                drawerOpen
                  ? "Close filter panel"
                  : activeFilterCount > 0
                    ? `Open filter panel — ${activeFilterCount} filter${activeFilterCount !== 1 ? "s" : ""} applied`
                    : "Open filter panel"
              }
              aria-expanded={drawerOpen}
              aria-label="Toggle filter panel"
            >
              <i className="fas fa-sliders-h" />
            </button>
            {/* Badge: number of active filters */}
            {activeFilterCount > 0 && (
              <span
                className="cpgv2-filter-badge"
                title={`${activeFilterCount} active filter${activeFilterCount !== 1 ? "s" : ""}`}
              >
                {activeFilterCount}
              </span>
            )}
          </div>
        )}
        {onRefresh != null && (
          <button
            type="button"
            className="cpgv2-icon-btn"
            onClick={onRefresh}
            title="Refresh"
            aria-label="Refresh"
          >
            <i className="fas fa-sync-alt" />
          </button>
        )}
        {title && <span className="cpgv2-header-title">{title}</span>}
      </div>

      {/* Right section: custom actions + refresh + show/hide + excel */}
      <div className="cpgv2-header-right">
        {renderActions?.(records)}

        {showHideColumns && columns.length > 0 && (
          <button
            type="button"
            className="cpgv2-icon-btn"
            onClick={() => setShowHideModalOpen(true)}
            title="Show / Hide Columns"
            aria-label="Show or hide columns"
          >
            <i className="fas fa-columns" />
          </button>
        )}

        {excelAdapter != null && (
          <button
            type="button"
            className="cpgv2-icon-btn"
            onClick={excelAdapter.export}
            title="Export to Excel"
            aria-label="Export to Excel"
          >
            <i className="fas fa-file-excel" />
          </button>
        )}
      </div>
    </div>
  );

  // Drawer content: render custom content if provided, otherwise render filter options if provided
  const renderDrawerContent = () => {
    // renderDrawer takes priority — the consumer controls everything inside
    if (renderDrawer) {
      return <div className="cpgv2-drawer-inner">{renderDrawer()}</div>;
    }

    if (filterOptions && filterOptions.length > 0) {
      return (
        <div className="cpgv2-drawer-inner">
          <CPGridV2Filters
            filterOptions={filterOptions}
            savedFilters={savedFilters}
            onSaveFilter={onSaveFilter}
            showSaveFilters={
              (showSaveFilters ??
                (savedFilters != null && savedFilters.length > 0)) ||
              onSaveFilter != null
            }
            onApply={(filters) => {
              lastAppliedFiltersRef.current = filters;

              const controls = adapter?.gridState?.controls;
              const sort = controls?.sort?.[0];

              // New filters always start back on page 1.
              onApplyFilters?.(filters, {
                take: controls?.take,
                skip: 0,
                sortBy: sort?.field,
                ascending: sort?.dir === "asc",
              });

              adapter?.resetPaging();
            }}
            onClear={() => {
              lastAppliedFiltersRef.current = {};
              onClearFilters?.();
              adapter?.resetPaging();
            }}
            onActiveCountChange={setActiveFilterCount}
          />
        </div>
      );
    }

    return null;
  };

  return (
    <div className="cpgv2-root" id={name} aria-label={title}>
      {/* Top header bar */}
      {renderHeader()}

      {/* Body: drawer + grid */}
      <div className="cpgv2-body">
        {/* Drawer panel */}
        {hasDrawer && (
          <div
            className={classNames("cpgv2-drawer", {
              "cpgv2-drawer--closed": !drawerOpen,
            })}
            style={
              {
                "--cpgv2-drawer-width": `${drawerWidth}px`,
              } as React.CSSProperties
            }
            aria-hidden={!drawerOpen}
          >
            {renderDrawerContent()}
          </div>
        )}

        {/* Grid content area */}
        <div className="cpgv2-content">
          <ExcelExport
            ref={excelAdapter?.ref}
            data={adapter?.exportData() ?? records.data}
          >
            <Grid
              {...kendoGridProps}
              size={size ?? "small"}
              filterable={false}
              style={{ height: "100%" }}
              reorderable={reorderable ?? true}
              resizable={resizable ?? true}
              sort={gridState?.controls.sort}
              pageable={getPaging()}
              skip={gridState?.controls.skip}
              take={gridState?.controls.take}
              total={records.total}
              data={records}
              onDataStateChange={handleGridStateChange}
              onSortChange={handleSortChange}
              onColumnReorder={(e) =>
                handleColumnUpdate({ updatedColumns: e.columns })
              }
              onColumnResize={(e) =>
                handleColumnUpdate({ updatedColumns: e.columns })
              }
              onRowClick={(e) => onRowClick?.(e.dataItem)}
              rowRender={handleRowRender}
              className={classNames(
                "cpgv2-grid",
                { "cpgv2-grid--compact": compact },
                { "cpgv2-grid--lines-rows": gridLines === "rows" },
                { "cpgv2-grid--lines-cells": gridLines === "cells" },
                { "cpgv2-grid--lines-none": gridLines === "none" },
                className,
              )}
            >
              {renderColumns()}
            </Grid>
          </ExcelExport>

          {/* Loading overlay */}
          <CPLoader showLoader={adapter?.showLoader ?? showLoader} />
        </div>
      </div>

      {/* Show/Hide columns modal */}
      {showHideModalOpen && columns.length > 0 && (
        <CPShowHideModal
          open
          onClose={() => setShowHideModalOpen(false)}
          columns={columns}
          onUpdate={(updated) => {
            adapter?.onColumnUpdate({
              columns: updated,
              updatedColumns: updated as any,
            });
            setShowHideModalOpen(false);
          }}
        />
      )}
    </div>
  );
};

export default CPGridV2;
